import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import AutoComplete from '../../components/molecules/AutoComplete';
import { OSIcons } from '../../utils/OS';
import AddFutureItems from '../containers/AddFutureItems';
import { translationPrefix } from '../utils';
import css from './AppSitePageStep.css';
import PicturizedMultiSelect from './PicturizedMultiSelect';
import StepCard from './StepCard';

const getFormattedSite = site => {
    const formattedPlatform = `${site.platform.charAt(0).toUpperCase()}${site.platform.slice(1)}`;
    const Icon = Object.entries(OSIcons).find(([key]) => key.toLowerCase() === site.platform.toLowerCase())?.[1];
    return {
        ...site,
        id: site.publicId,
        displayName: `${formattedPlatform} - ${site.publicId}`,
        Icon,
    };
};

const AppSitePageStep = ({
    apps = [],
    onChange = () => {},
    enableAddFuture = () => {},
    onStepValidationStatusChanged = () => {},
    translate = () => {},
}) => {
    const [appFilter, setAppFilter] = useState('');
    const selectedAppsCount = apps.filter(app => app.selected).length;
    const selectedAppSitesCount = apps.flatMap(app => app.sites.filter(site => site.selected)).length;
    const disabledAppsCount = apps.filter(app => app.disabled).length;
    const totalAvailableAppsCount = apps.length;

    const appSitePublicIds = useMemo(
        () =>
            apps
                .filter(app => !app.disabled)
                .flatMap(app => app.sites.filter(site => !site.disabled).map(site => site.publicId)),
        [apps]
    );
    const formattedApps = useMemo(
        () =>
            apps.map(app => ({
                ...app,
                subItems: app.sites.map(getFormattedSite),
                ...(app.icon && {
                    Icon: 'img',
                    iconProps: { src: app.icon, alt: app.name },
                }),
            })),
        [apps]
    );

    useEffect(() => {
        const isValid = selectedAppSitesCount > 0;
        onStepValidationStatusChanged({
            isValid,
            title: isValid
                ? ''
                : translate('STATIC.PAGES.TEAM_MEMBERS.SELECT_AT_LEAST_ONE_TOOLTIP', {
                      itemType: 'appsite',
                  }),
        });
    }, [onStepValidationStatusChanged, translate, selectedAppSitesCount]);

    const selectAll = value => {
        onChange(appSitePublicIds, value);
        enableAddFuture();
    };

    const onItemSelected = (selectedId, value, isAppSite = false) => {
        if (isAppSite) {
            onChange([selectedId], value);
        } else {
            const app = apps.find(({ id }) => id === selectedId);
            if (app) {
                onChange(
                    app.sites.map(site => site.publicId),
                    value
                );
            }
        }
    };

    const textPredicate = app => app.name.toLocaleLowerCase().includes(appFilter.toLocaleLowerCase().trim());

    return (
        <div className={css.container}>
            <StepCard
                className={css.stepCard}
                title={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.TITLE.APPS`}
                titleTextReplace={{
                    count: selectedAppsCount,
                    total: totalAvailableAppsCount - disabledAppsCount,
                }}
                description={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.DESCRIPTION.APPS`}
            >
                <div>
                    <AutoComplete
                        placeholder={translate(`${translationPrefix}.PLACEHOLDER.APP_SEARCH`)}
                        selectOptions={{
                            options: [],
                            noOptionsMessage: () => null,
                            isSearchable: true,
                            inputValue: appFilter,
                        }}
                        onInputChange={setAppFilter}
                        controlledValue={appFilter}
                    />
                </div>
                <PicturizedMultiSelect
                    className={css.multiSelectContainer}
                    allItems={formattedApps}
                    filteredItems={formattedApps.filter(textPredicate)}
                    onChange={onItemSelected}
                    selectAll={selectAll}
                    itemType="appsites"
                />
            </StepCard>
            <AddFutureItems itemType="appsite" form="app_site_id" dimensionFilter className={css.addFutureItems} />
        </div>
    );
};

AppSitePageStep.propTypes = {
    apps: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string.isRequired,
            icon: PropTypes.string.isRequired,
            selected: PropTypes.bool.isRequired,
            sites: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    publicId: PropTypes.string.isRequired,
                    platform: PropTypes.string.isRequired,
                    icon: PropTypes.string.isRequired,
                })
            ),
        })
    ),
    onChange: PropTypes.func,
    enableAddFuture: PropTypes.func,
    onStepValidationStatusChanged: PropTypes.func,
    translate: PropTypes.func,
};

export default AppSitePageStep;
